<script setup lang="ts">
import {ICellEditorParams} from "ag-grid-community";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import {computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef} from "vue";
import BigTextFieldWidgetUtil from "@/utils/editView/widgets/BigTextFieldWidgetUtil.ts";
import type {QBtnDropdown, QInput, QList} from "quasar";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {onClickOutside} from "@vueuse/core";
import Translator from "@/utils/common/Translator.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import {SuggestionProviderItemsWithOptions} from "@/utils/presenters/interfaces.ts";
import {useInputRestrictions} from "@/composables/useInputRestrictions.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";

const props = defineProps<{
  params: ICellEditorParams &
    {
      maxLength: number,
      colId: EditScreenGridColumn["id"],
      handler: () => EditScreenGridHandler,
      columnMetaData: FieldMetaData,
      lang: { value: string }
    }
}>()

const colId = props.params.colId || "";
const handler = props.params.handler();
const textAreaWrapperRef = useTemplateRef<HTMLDivElement>("textAreaWrapperRef");
const bigTextFieldStyle = ClientCache.getFieldStyle(handler.entityType, props.params.columnMetaData?.fieldId ?? '');
useInputRestrictions(textAreaWrapperRef, bigTextFieldStyle?.fixedInputLanguage, bigTextFieldStyle?.fixedLetterCase);
const columnMetaData = props.params.columnMetaData
const fieldUtil = new BigTextFieldWidgetUtil(handler.presenter, columnMetaData, props.params);
fieldUtil.onSpeechLanguageChange(props.params.lang.value);
const qInputRef = useTemplateRef<QInput>("qInputRef");
const qListRef = useTemplateRef<QList>("qListRef");
const qListRef2 = useTemplateRef<QList>("qListRef2");
const qBtnDropdownModel = ref(false);
const allowedValuesList = ref<SuggestionProviderItemsWithOptions[] | undefined>(undefined);
const showAllowedValuesMenu = ref(false);

onClickOutside(textAreaWrapperRef, onBlur, {
  ignore: [qListRef, qListRef2]
})

const canShowMicrophone = computed(() => qBtnDropdownModel.value || fieldUtil.speech.isListening.value)

const isColumnDisabled = (): boolean => {
  return handler.presenter.isColumnDisabled(props.params.data.id, handler.gridId, colId)
}

async function onInputDblClick() {
  if (!allowedValuesList.value)
    await fieldUtil.suggest(fieldUtil.fetchFieldValue(), allowedValuesList);
  if (allowedValuesList.value && allowedValuesList.value.length > 0)
    showAllowedValuesMenu.value = true;
}

async function onAllowedValueSelected(value: string) {
  fieldUtil.onPartialUserInput(value);
  await fieldUtil.onUserInputFinished();
  props.params.stopEditing();
  showAllowedValuesMenu.value = false;
}

function getValue() {
  return fieldUtil.inputFieldValueRef.value;
}

function namaUpdateValue(value: string) {
  fieldUtil.inputFieldValueRef.value = value;
}

async function onBlur() {
  if (FieldWidgetUtil.focusedField === fieldUtil)
    FieldWidgetUtil.focusedField = null;
  fieldUtil.isFieldFocused.value = false;
  return fieldUtil.onUserInputFinished().then(() => {
    props.params.lang.value = fieldUtil.lang.value;
    props.params?.stopEditing();
  });
}

function onKeydown(e: KeyboardEvent) {
  if (e.key && e.key.startsWith("Arrow"))
    e.stopPropagation();
  if (e.key != "Enter")
    return;
  if (AGGridUtils.isBulkAction(e)) {
    namaUpdateValue(getValue());
    AGGridUtils.dispatchBulkEventFor(qInputRef);
  } else if (e.ctrlKey || e.altKey) {
    e.stopPropagation();
    fieldUtil.insertNewLine(qInputRef);
  }
}

onMounted(async () => {
  await nextTick();
  const qInputRefValue = qInputRef.value;
  if (!qInputRefValue)
    return;
  setTimeout(() => {
    qInputRefValue.focus();
    qInputRefValue.select();
    if (props.params?.eventKey?.length === 1)
      fieldUtil.inputFieldValueRef.value = props.params.eventKey;
  })
})

onUnmounted(() => {
  onBlur();
})

defineExpose({
  getValue, namaUpdateValue
})
</script>

<template>
  <div class="tw-mb-0 tw-h-full tw-w-full" ref="textAreaWrapperRef" :dir="Translator.fetchDir()">
    <div class="tw-relative tw-leading-tight tw-w-full tw-h-full">
      <q-input
        outlined dense hide-bottom-space
        :id="colId"
        ref="qInputRef"
        :maxlength="params.maxLength" type="textarea"
        autocomplete="off"
        @focus="fieldUtil.onFocus"
        @blur="onBlur"
        class="ag-input-field-input ag-text-area-input ag-big-text-class control-big-text-microphone-opacity"
        :class="[isColumnDisabled()?'nama-field-input-disabled':'nama-field-input']"
        input-class="tw-bg-transparent tw-font-normal !tw-resize-none tw-text-base dark:tw-text-white focus:tw-ring-transparent focus:tw-ring-0"
        v-model="fieldUtil.inputFieldValueRef.value"
        @keydown="onKeydown"
        @dblclick="onInputDblClick">
        <q-menu v-model="showAllowedValuesMenu" no-parent-event fit>
          <q-list dense ref="qListRef2">
            <q-item
              v-for="(val, idx) in allowedValuesList"
              :key="idx" clickable v-close-popup
              @click.stop.prevent="onAllowedValueSelected(fieldUtil.getSuggestionText(val))">
              <q-item-section v-bind:dir="val.optionDirection">
                <q-item-label v-text="Translator.translateId(fieldUtil.getSuggestionText(val))" />
                <q-item-label caption class="dark:tw-text-[rgb(255,255,255,0.5)]"
                              v-if="SearchingAndSuggestionUtil.hasDescriptor(val as EntityReferenceData)" v-text="val.optionCaption" />
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
        <template #append>
          <div class="tw-h-fit tw-bottom-3 tw-pt-3 tw-flex tw-z-10 tw-gap-2 tw-absolute tw-right-2">
            <q-btn-dropdown
              size="xs"
              padding="8px"
              split flat
              @beforeShow="qBtnDropdownModel=true"
              @beforeHide="qBtnDropdownModel=false"
              unelevated
              :data-big-text-is-focused="fieldUtil.isFieldFocused.value?'':null"
              :disable="isColumnDisabled() || !fieldUtil.speech.isSupported.value"
              class="q-btn-dropdown-style"
              :class="[canShowMicrophone?'!tw-opacity-100':'microphone']"
              @click.stop="fieldUtil.startSpeechRecognition()">
              <template #label>
                <NamaIcon
                  class="tw-size-5 active-primary"
                  :default-icon="fieldUtil.speech.isListening.value?'nm-wave':'nm-microphone'"/>
              </template>
              <q-list ref="qListRef">
                <q-item dense class="tw-px-2" :class="{'selected-list-item':fieldUtil.lang.value==item.value}"
                        @click="fieldUtil.onSpeechLanguageChange(item.value)"
                        clickable
                        v-close-popup
                        v-for="item in BigTextFieldWidgetUtil.speechLanguages">
                  <q-item-section>
                    <q-item-label class="tw-text-xs" v-text="Translator.translateId(item.value)"/>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>
        </template>
      </q-input>
    </div>
  </div>
</template>
